// 🚀 GLOBAL DATA STORE - INSTANT DATA ACCESS
// Preloads all data and makes it instantly available to eliminate micro-loading

import { simpleCache } from './simpleCache';
import { supabase } from '../integrations/supabase/client';

interface GlobalDataState {
  profile: any | null;
  connections: any[] | null;
  secondDegreeConnections: any[] | null; // 🚀 V97.0 OPTIMIZATION: Add 2nd degree connections
  conversations: any[] | null;
  discover: any[] | null;
  settings: any | null;
  isPreloaded: boolean;
  lastUpdated: number;
}

class GlobalDataStore {
  private static instance: GlobalDataStore;
  private state: GlobalDataState = {
    profile: null,
    connections: null,
    secondDegreeConnections: null, // 🚀 V97.0 OPTIMIZATION: Add 2nd degree connections
    conversations: null,
    discover: null,
    settings: null,
    isPreloaded: false,
    lastUpdated: 0
  };

  private listeners: ((state: GlobalDataState) => void)[] = [];

  static getInstance(): GlobalDataStore {
    if (!GlobalDataStore.instance) {
      GlobalDataStore.instance = new GlobalDataStore();
    }
    return GlobalDataStore.instance;
  }

  // 🚀 INSTANT: Get data without any loading
  getProfile(): any | null {
    return this.state.profile;
  }

  getConnections(): any[] | null {
    return this.state.connections;
  }

  // 🚀 V97.0 OPTIMIZATION: Get 2nd degree connections
  getSecondDegreeConnections(): any[] | null {
    return this.state.secondDegreeConnections;
  }

  getConversations(): any[] | null {
    return this.state.conversations;
  }

  getDiscover(): any[] | null {
    return this.state.discover;
  }

  getSettings(): any | null {
    return this.state.settings;
  }

  // 🚀 INSTANT: Check if data is available
  isDataAvailable(): boolean {
    return this.state.isPreloaded;
  }

  // 🚀 INSTANT: Check if data is ready for immediate use
  isReady(): boolean {
    return this.state.isPreloaded && this.state.connections !== null;
  }

  // 🚀 PRELOAD: Load all data once and cache it
  async preloadAllData(userId: string): Promise<void> {
    // Use intelligent logging system
    if (import.meta.env.DEV) {
      console.log('🚀 GLOBAL-STORE: Preloading all data for instant access...');
    }

    try {
      // Load profile
      const profileKey = `user_profile_${userId}`;
      let profile = simpleCache.get(profileKey);
      if (!profile) {
        if (process.env.NODE_ENV === 'development') {
          if (import.meta.env.DEV) {
        console.log('🚀 GLOBAL-STORE: Loading profile from database...');
      }
        }
        // Load from database if not in cache
        profile = await this.loadProfileFromDatabase(userId);
        if (profile) {
          // 🚀 V96.0 OPTIMIZATION: 24-hour session persistence
          simpleCache.set(profileKey, profile, 24 * 60 * 60 * 1000); // 24 hours
        }
      }

      // Load connections
      const connectionsKey = `connections_${userId}`;
      let connections = simpleCache.get(connectionsKey);
      if (!connections) {
        if (process.env.NODE_ENV === 'development') {
          if (import.meta.env.DEV) {
        console.log('🚀 GLOBAL-STORE: Loading connections from database...');
      }
        }
        connections = await this.loadConnectionsFromDatabase(userId);
        if (connections) {
          // 🚀 V96.0 OPTIMIZATION: 24-hour session persistence
        simpleCache.set(connectionsKey, connections, 24 * 60 * 60 * 1000); // 24 hours
        }
      }

      // Load conversations (derived from connections)
      const conversationsKey = `conversations_${userId}`;
      let conversations = simpleCache.get(conversationsKey);
      if (!conversations && connections) {
        if (process.env.NODE_ENV === 'development') {
          if (import.meta.env.DEV) {
        console.log('🚀 GLOBAL-STORE: Processing conversations...');
      }
        }
        conversations = this.processConnectionsToConversations(Array.isArray(connections) ? connections : [], userId);
        simpleCache.set(conversationsKey, conversations, 5 * 60 * 1000); // 5 minutes
      }

      // Load discover data
      const discoverKey = `discover_${userId}`;
      let discover = simpleCache.get(discoverKey);
      if (!discover) {
        if (process.env.NODE_ENV === 'development') {
          if (import.meta.env.DEV) {
        console.log('🚀 GLOBAL-STORE: Loading discover data...');
      }
        }
        discover = await this.loadDiscoverFromDatabase(userId);
        if (discover) {
          // 🚀 V96.0 OPTIMIZATION: 24-hour session persistence
        simpleCache.set(discoverKey, discover, 24 * 60 * 60 * 1000); // 24 hours
        }
      }

      // Load settings
      const settingsKey = `settings_${userId}`;
      let settings = simpleCache.get(settingsKey);
      if (!settings) {
        if (process.env.NODE_ENV === 'development') {
          if (import.meta.env.DEV) {
        console.log('🚀 GLOBAL-STORE: Loading settings...');
      }
        }
        settings = await this.loadSettingsFromDatabase(userId);
        if (settings) {
          // 🚀 V96.0 OPTIMIZATION: 24-hour session persistence
        simpleCache.set(settingsKey, settings, 24 * 60 * 60 * 1000); // 24 hours
        }
      }

      // Update global state
      this.state = {
        profile: profile || null,
        connections: Array.isArray(connections) ? connections : [],
        secondDegreeConnections: [],
        conversations: Array.isArray(conversations) ? conversations : [],
        discover: Array.isArray(discover) ? discover : [],
        settings: settings || null,
        isPreloaded: true,
        lastUpdated: Date.now()
      };

      if (process.env.NODE_ENV === 'development') {
        if (import.meta.env.DEV) {
        console.log('✅ GLOBAL-STORE: All data preloaded and ready for instant access');
      }
      }
      this.notifyListeners();

    } catch (error) {
      console.error('❌ GLOBAL-STORE: Error preloading data:', error);
    }
  }

  // 🚀 INSTANT: Update specific data
  updateProfile(profile: any): void {
    this.state.profile = profile;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  updateConnections(connections: any[]): void {
    this.state.connections = connections;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  // 🚀 V97.0 OPTIMIZATION: Update 2nd degree connections
  updateSecondDegreeConnections(secondDegreeConnections: any[]): void {
    this.state.secondDegreeConnections = secondDegreeConnections;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  updateConversations(conversations: any[]): void {
    this.state.conversations = conversations;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  updateDiscover(discover: any[]): void {
    this.state.discover = discover;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  updateSettings(settings: any): void {
    this.state.settings = settings;
    this.state.lastUpdated = Date.now();
    this.notifyListeners();
  }

  // 🚀 INSTANT: Subscribe to data changes
  subscribe(listener: (state: GlobalDataState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // 🚀 INSTANT: Get current state
  getState(): GlobalDataState {
    return { ...this.state };
  }

  // 🚀 INSTANT: Clear all data
  clear(): void {
    this.state = {
      profile: null,
      connections: null,
      secondDegreeConnections: null, // 🚀 V97.0 OPTIMIZATION: Add 2nd degree connections
      conversations: null,
      discover: null,
      settings: null,
      isPreloaded: false,
      lastUpdated: 0
    };
    this.notifyListeners();
  }

  // Private methods for database loading
  private async loadProfileFromDatabase(userId: string): Promise<any> {
    try {
      const { data: profile, error } = await supabase
        .from('users')
        .select('id, name, email, photo_urls, bio, trust_score')
        .eq('id', userId)
        .single();
        
      if (error) {
        console.error('❌ GLOBAL-STORE: Error loading profile:', error);
        return null;
      }
      
      return profile;
    } catch (error) {
      console.error('❌ GLOBAL-STORE: Error loading profile:', error);
      return null;
    }
  }

  private async loadConnectionsFromDatabase(userId: string): Promise<any[]> {
    try {
      console.log('🚀 V161.13: GLOBAL-STORE using connection coordinator');
      // V161.13: Use connection coordinator instead of network service
      const { getFullConnections } = await import('../utils/connectionCoordinator');
      const connectionsData = await getFullConnections(userId);

      // Convert to expected format
      const allConnections = [
        ...connectionsData.firstDegree,
        ...connectionsData.secondDegree,
        ...connectionsData.thirdDegree
      ];

      console.log(`✅ V161.13: GLOBAL-STORE loaded ${allConnections.length} connections via coordinator`);
      return allConnections;
    } catch (error) {
      console.error('❌ GLOBAL-STORE: Error loading connections:', error);
      return [];
    }
  }

  private async loadDiscoverFromDatabase(userId: string): Promise<any[]> {
    try {
      // For now, return empty array - discover data can be loaded later
      return [];
    } catch (error) {
      console.error('❌ GLOBAL-STORE: Error loading discover data:', error);
      return [];
    }
  }

  private async loadSettingsFromDatabase(userId: string): Promise<any> {
    try {
      // For now, return default settings
      return {
        notifications: true,
        privacy: 'public',
        theme: 'dark'
      };
    } catch (error) {
      console.error('❌ GLOBAL-STORE: Error loading settings:', error);
      return null;
    }
  }

  private processConnectionsToConversations(connections: any[], userId: string): any[] {
    // 🚨 CRITICAL FIX: Add null/undefined check before array operations
    if (!connections || !Array.isArray(connections)) {
      console.warn('⚠️ GLOBAL-STORE: Invalid connections data received:', connections);
      return [];
    }

    return connections.map(conn => ({
      id: conn.id,
      name: conn.name || 'Unknown User',
      email: conn.email || '',
      lastMessage: "Start chatting...",
      timestamp: "Now",
      unread: 0,
      avatar: conn.photo_urls?.[0] || '/default-avatar.svg',
      hasChat: false,
      userId: conn.user_id || conn.id,
      type: conn.type || 'friend'
    }));
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener(this.state));
  }
}

// 🚀 GLOBAL: Export singleton instance
export const globalDataStore = GlobalDataStore.getInstance();

export default globalDataStore; 